import { useState } from 'react';
import YouTubePlayer from 'react-player/youtube';

import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useCurrentuser } from '@uplift/contexts';
import { useIsMobile, useL10n, useLocalFeatureFlag } from '@uplift/hooks';
import { UpliftBetterHelpStackedIcon } from '@uplift/icons';
import { Modal, ModalActions, ModalContent, ModalProgressButton, ModalTitle } from '@uplift/ui-v2';

import { BetterDesktopExperience } from '../components';

export type WelcomeProviderModalProps = {
  videoUrl: string;
  handleClick: () => void;
};

export const WelcomeProviderModal: React.VFC<WelcomeProviderModalProps> = ({ videoUrl, handleClick }) => {
  const [ isVideoEnded, setIsVideoEnded ] = useState(false);
  const PROVIDER_WELCOME_MODAL_SHOW_GET_READY_BUTTON = useLocalFeatureFlag('PROVIDER_WELCOME_MODAL_SHOW_GET_READY_BUTTON');
  const [ _showGetStartedButton, setShowGetStartedButton ] = useState(false);
  const showGetStartedButton = _showGetStartedButton || PROVIDER_WELCOME_MODAL_SHOW_GET_READY_BUTTON;
  const [ showBetterDesktopExperience, setShowBetterDesktopExperience ] = useState(false);
  const user = useCurrentuser();

  const handleVideoEnd = () => {
    setIsVideoEnded(true);
    setShowGetStartedButton(true);
  };

  const isMobile = useIsMobile();

  const getStarted = () => {
    if (isMobile) {
      setShowBetterDesktopExperience(true);
      return;
    }
    handleClick();
  };

  const { classes } = useStyles();
  const l10n = useL10n();
  const providerName = user.firstName;

  if (isVideoEnded && showBetterDesktopExperience) {
    return <BetterDesktopExperience isMobile={isMobile} handleClick={handleClick} />;
  }

  return (
    <Modal
      open
      showCloseIcon={false}
      classes={{ paperWidthLg: classes.paperWidthLg }}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <ModalTitle classes={{ title: classes.title }}>
        {isMobile && (
          <Box className={classes.logo} mb={5}>
            <UpliftBetterHelpStackedIcon />
          </Box>
        )}
        {`${l10n.general.welcome}, ${providerName}! `}
      </ModalTitle>
      <ModalContent className={classes.content}>
        <Typography color="textSecondary">{l10n.providerSettings.onboarding.introductoryVideo}</Typography>
        <Box pt={3} pb={!showGetStartedButton && '70px'}>
          <YouTubePlayer
            url={videoUrl}
            onEnded={handleVideoEnd}
            width="100%"
            controls
          />
        </Box>
      </ModalContent>
      {showGetStartedButton && (
        <>
          {isMobile && <Divider className={classes.divider} />}
          <ModalActions className={isMobile ? classes.mobileModalActions : null}>
            <ModalProgressButton variant="contained" color="secondary" onClick={getStarted} fullWidth={isMobile} >
              {l10n.onboarding.welcome.getStartedButton}
            </ModalProgressButton>
          </ModalActions>
        </>
      )}
    </Modal>
  );
};

const useStyles = makeStyles()(theme => ({
  title: {
    color: theme.palette.darkSlate.main,
    fontSize: '26px',
    paddingTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      fontSize: '32px',
      paddingLeft: theme.spacing(2),
    }
  },
  paperWidthLg: {
    [theme.breakpoints.up('sm')]: {
      maxWidth: '600px'
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: '850px'
    }
  },
  content: {
    marginTop: theme.spacing(-4),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    }
  },
  iframe: {
    width: '100%',
    aspectRatio: '16/9',
  },
  getStartedButton: {
    display: 'block',
    marginTop: 30,
    marginLeft: 'auto',
  },
  mobileModalActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
    flexDirection: 'column-reverse',
    '& *': {
      marginLeft: '0 !important',
    }
  },
  divider: {
    display: 'flex',
    width: '100%',
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    color: theme.palette.darkSlate.main
  },
}));
